import AddEditTaskTemplateDialog from "@/components/Forms/SubForms/AddEditTaskTempalteDialog.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useDeleteTaskTemplate } from "@/mutations/useDeleteTaskTemplate.tsx";
import { type MrtTemplate, useFetchMrtTemplates } from "@/queries/useFetchMrtTaskTemplates.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Stack, TextField } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

const MrtTaskTemplatesTable = () => {
    const { hasPermission } = useEmployee();
    const deleteTaskTemplate = useDeleteTaskTemplate();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [editTaskTemplateId, setEditTaskTemplateId] = useState<string | undefined>();
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    const { data, isError, isRefetching, isLoading } = useFetchMrtTemplates({
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const handleRowClick = (row: MRT_Row<MrtTemplate>) => {
        if (hasPermission("edit:task-template")) {
            setEditTaskTemplateId(row.original.idUUID);
        }
    };

    const handleDeleteRow = (row: MRT_Row<MrtTemplate>) => {
        if (window.confirm("Are you sure you want to delete this TaskTemplate?")) {
            deleteTaskTemplate.mutate(
                {
                    taskTemplateUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("TaskTemplate removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: TaskTemplate could not be removed.", {
                            variant: "error",
                        });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const columns = useMemo<MRT_ColumnDef<MrtTemplate>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtTemplate>[] = [
            {
                accessorKey: "displayName",
                header: "Name",
            },
            {
                accessorKey: "description",
                header: "Description",
            },
            {
                accessorKey: "duration",
                header: "Duration",
                Cell: ({ row }) => {
                    // If duration is 0, show "N/A" (no due date)
                    if (row.original.duration === "0") {
                        return "N/A";
                    }

                    if (row.original.duration && row.original.durationUnit) {
                        return `${row.original.duration} ${row.original.durationUnit}`;
                    }

                    return "";
                },
            },
        ];

        if (hasPermission("delete:task-template")) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [hasPermission]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data.data,

        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        // top toolbar
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,

        // server side searching and pagination
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,

        // state
        rowCount: data?.meta.totalRowCount ?? 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },

        renderTopToolbar: ({ table }) => (
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ padding: "0.5rem" }}
            >
                <TextField
                    placeholder="Search..."
                    value={table.getState().globalFilter || ""}
                    onChange={(e) => table.setGlobalFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                />

                <AddEditTaskTemplateDialog
                    showAddButton={true}
                    editTaskTemplateId={editTaskTemplateId}
                    clearEditTaskTemplateId={() => {
                        setEditTaskTemplateId(undefined);
                    }}
                />
            </Stack>
        ),

        // design
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<MrtTemplate> }) => ({
            onClick: () => handleRowClick(row),
            sx: { cursor: "pointer" },
        }),
    });

    return <MaterialReactTable table={table} />;
};

export default MrtTaskTemplatesTable;
